import { Fragment, useMemo } from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  formatCompact,
  periodLabel,
  periodLabelLong,
  toNumber,
  type ActivityOrganisation,
  type ActivityPeriod,
  type Granularity,
} from "./activityResources";

// Single-hue ramp from the surface to slot 1, mixed in oklab: oklch would swing the hue through pink on the way.
const LEVEL_MIX = [0, 22, 42, 66, 100];

function levelFor(value: number, thresholds: number[]): number {
  if (value <= 0) return 0;
  return 1 + thresholds.filter((t) => value > t).length;
}

/** Quartile cut points over the non-zero cells, so one very busy organisation doesn't wash everyone else out. */
function quartiles(values: number[]): number[] {
  const sorted = values.filter((v) => v > 0).sort((a, b) => a - b);
  if (sorted.length === 0) return [];
  const at = (q: number) => sorted[Math.min(sorted.length - 1, Math.floor(q * sorted.length))]!;
  return [at(0.25), at(0.5), at(0.75)];
}

export function ActivityHeatmap({
  periods,
  organisations,
  granularity,
  selected,
  showClient,
  onSelect,
}: {
  periods: ActivityPeriod[];
  organisations: ActivityOrganisation[];
  granularity: Granularity;
  selected: string | null;
  showClient: boolean;
  onSelect: (periodStart: string, organisationId?: string) => void;
}) {
  const rows = useMemo(
    () =>
      organisations
        .map((org) => {
          const byStart = new Map(
            (granularity === "months" ? org.months : org.weeks).map((p) => [p.start, toNumber(p.total)]),
          );
          return { org, values: periods.map((p) => byStart.get(p.start) ?? 0) };
        })
        .filter((row) => row.values.some((v) => v > 0)),
    [organisations, periods, granularity],
  );
  const thresholds = useMemo(() => quartiles(rows.flatMap((r) => r.values)), [rows]);
  const lastStart = periods[periods.length - 1]?.start;

  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground py-8 text-center text-sm">
        No organisation created anything in this window.
      </p>
    );
  }

  return (
    <div>
      <div className="max-h-[30rem] overflow-auto rounded-md border">
        <div
          className="grid min-w-max text-xs"
          style={{ gridTemplateColumns: `minmax(12rem, 18rem) repeat(${periods.length}, minmax(2.75rem, 1fr))` }}
          role="grid"
          aria-label="Records created per organisation and period"
        >
          <div className="bg-card text-muted-foreground sticky top-0 left-0 z-20 border-b px-3 py-2 font-medium">
            Organisation
          </div>
          {periods.map((p) => (
            <button
              key={p.start}
              type="button"
              onClick={() => onSelect(p.start)}
              aria-pressed={p.start === selected}
              className={cn(
                "bg-card text-muted-foreground hover:text-foreground sticky top-0 z-10 border-b px-1 py-2 text-center font-medium tabular-nums",
                p.start === selected && "text-foreground bg-accent",
                p.start === lastStart && "italic",
              )}
            >
              {periodLabel(p.start, granularity)}
            </button>
          ))}

          {rows.map(({ org, values }) => (
            <Fragment key={org.organisationId}>
              <div className="bg-card sticky left-0 z-10 min-w-0 border-b px-3 py-1.5">
                <Link to={`/organisations/${org.organisationId}`} className="block truncate font-medium hover:underline">
                  {org.organisationName}
                </Link>
                {showClient && <p className="text-muted-foreground truncate text-[11px]">{org.clientName}</p>}
              </div>
              {values.map((value, i) => {
                const period = periods[i]!;
                const level = levelFor(value, thresholds);
                return (
                  <button
                    key={period.start}
                    type="button"
                    onClick={() => onSelect(period.start, org.organisationId)}
                    aria-label={`${org.organisationName}, ${periodLabelLong(period.start, granularity)}: ${value} created`}
                    className={cn(
                      "m-0.5 flex items-center justify-center rounded-[3px] tabular-nums transition-shadow hover:ring-2 hover:ring-[var(--series-1)]",
                      level === 0 ? "bg-muted/50" : level >= 3 ? "text-white" : "text-foreground",
                      period.start === selected && "ring-foreground/40 ring-1",
                    )}
                    style={
                      level > 0
                        ? { background: `color-mix(in oklab, var(--series-1) ${LEVEL_MIX[level]}%, var(--viz-surface))` }
                        : undefined
                    }
                  >
                    {value > 0 ? formatCompact(value) : ""}
                  </button>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
      <div className="text-muted-foreground mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
        <span>
          Cells count records created. The {granularity === "months" ? "month" : "week"} in italics is still in
          progress. Select a cell or column to see the breakdown below.
        </span>
        <span className="flex items-center gap-1" aria-hidden>
          Fewer
          {LEVEL_MIX.slice(1).map((mix) => (
            <span
              key={mix}
              className="size-3 rounded-[2px]"
              style={{ background: `color-mix(in oklab, var(--series-1) ${mix}%, var(--viz-surface))` }}
            />
          ))}
          More
        </span>
      </div>
    </div>
  );
}
